import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses';
import { invalidValueMsg, t } from '../../common/i18n';
import { PhoneNumber } from '../../common/react-hook-form/fields';
import {
  PHONE_NUMBER_REGEX,
  forceInternationalPhoneNumberFormat,
  formatPhoneNumber,
} from '../../common/utils/phoneNumberUtils';

export interface RespondentListProps {
  numbers?: string[];
  name?: string;
  edit?: boolean;
  getError?: () => string | undefined;
  isFirstRequired?: boolean;
  numberOfRespondents?: number;
  respondentLabel: string;
  originalNumbers?: string[];
  subscriptionUserFriendlyId?: string;
}

interface NumberViewOrEditProps {
  label: string;
  name: string;
  number: string;
  edit: boolean;
  error?: string;
  required?: boolean;
  originalNumber?: string;
  subscriptionUserFriendlyId?: string;
}

export const validatePhoneNumber = (value: string, subscriptionUserFriendlyId?: string, originalNumber?: string) => {
  if (!value) {
    return undefined;
  }

  // This is a special case
  // If the both value and original number is `0100` followed by the subscription user-friendly ID then this is allowed,
  const speciallyAllowedNumber = `010${subscriptionUserFriendlyId}`;
  const isOriginalNumberAllowed = originalNumber === speciallyAllowedNumber;
  const isValueSameAsOriginalNumber = value === originalNumber;
  if (isOriginalNumberAllowed && isValueSameAsOriginalNumber) {
    return undefined;
  } else if (!PHONE_NUMBER_REGEX.test(value)) {
    return t.DUPA(invalidValueMsg);
  }

  const invalidPrefixes = ['116', '100', '200', '2020', '2022', '2023', '300', '600', '601', '602', '700', '900'];
  const invalidPrefixesWithCountryCode = invalidPrefixes
    .concat(invalidPrefixes.map(pre => `+358${pre}`))
    .concat(invalidPrefixes.map(pre => `358${pre}`))
    .concat(invalidPrefixes.map(pre => `0${pre}`));

  const number = forceInternationalPhoneNumberFormat(value.replace(/\s/g, ''))?.slice(4);
  return invalidPrefixesWithCountryCode.some(pre => number?.startsWith(pre))
    ? t.PG0L('Number is not allowed')
    : undefined;
};

const NumberViewOrEdit = ({
  label,
  name,
  number,
  edit,
  required = false,
  error,
  originalNumber,
  subscriptionUserFriendlyId,
}: NumberViewOrEditProps) => {
  return (
    <div className="of-pbx-accordion-content__number">
      {edit ? (
        <>
          <PhoneNumber
            name={name}
            required={required}
            label={label}
            validate={(value: string) => validatePhoneNumber(value, subscriptionUserFriendlyId, originalNumber)}
          />
          {error && <p className={dsClass.INPUTERROR}>{error}</p>}
        </>
      ) : (
        <>
          <div className={`${dsClass.PADDING_BOTTOM_1}`}>
            <label className={dsClass.INPUT_LABELAREA_LABEL}>{label}</label>
          </div>
          {formatPhoneNumber(number || '—')}
        </>
      )}
    </div>
  );
};

export const RespondentList = ({
  numbers = [],
  name,
  edit = false,
  getError,
  isFirstRequired = false,
  respondentLabel,
  numberOfRespondents = 4,
  originalNumbers,
  subscriptionUserFriendlyId,
}: RespondentListProps) => {
  return (
    <>
      {Array.from(Array(numberOfRespondents).keys()).map(number => (
        <CL.GridCol
          colsXS={4}
          colsS={6}
          colsM={3}
          colsL={3}
          colsXL={3}
          className={dsClass.PADDING_BOTTOM_2}
          key={number}
        >
          <NumberViewOrEdit
            name={`${name}[${number}]`}
            number={numbers[number]}
            label={`${respondentLabel} ${number + 1}`}
            edit={edit}
            required={number === 0 && isFirstRequired}
            error={number === 0 && getError ? getError() : undefined}
            originalNumber={originalNumbers ? originalNumbers[number] : undefined}
            subscriptionUserFriendlyId={subscriptionUserFriendlyId}
          />
        </CL.GridCol>
      ))}
    </>
  );
};
